import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDrawerOpen } from '../../store/global';
import { fetchImage } from '../../utils/img';
import { getAvatarBackgroundStyle, getId, getItemPicAsset } from '../../utils/item';
import { renderIconBySlug } from '../../content/icons';
import { NumGlobal } from '../num/num.Global';
import { ConditionGlobal } from '../condition/condition.Global';
import { TextMd } from '../text/text.Md';
import { normalizeSecondaryEntries } from './link.DisplayOptions';

export const LinkShort = props => {
  const {
    linkId,
    elementsToDisplay,
    noteNumEnabled = false,
    noteNumPosition = 'top-right',
    noteNum = 0,
    onChangeNoteNum = null,
    noteCanEdit = false,
    reducedMinAvatarSize = 0,
  } = props;

  const link = useSelector(state => state.items[linkId]);
  const dispatch = useDispatch();
  const [avatar, setAvatar] = useState('');
  const [avatarMask, setAvatarMask] = useState('');
  const [rotatedAvatarMask, setRotatedAvatarMask] = useState('');
  const [avatarBgColor, setAvatarBgColor] = useState(null);
  const [avatarIconSlug, setAvatarIconSlug] = useState(null);
  const [editingNoteNum, setEditingNoteNum] = useState(false);
  const [noteDraft, setNoteDraft] = useState('0');
  const [display, setDisplay] = useState({
    avatar: {
      checked: true,
      type: 'rond',
      maskPicIndex: null,
      maskFit: 'contain',
      maskInvert: false,
      maskRotate: 0,
    },
    label: { checked: true, position: 'bas' },
    secondary: [],
    numbers: [],
  });
  // const [linkNums, setLinkNum] = useState([]);

  const ref = useRef(null);

  useEffect(() => {
    const safe = Number.isFinite(noteNum) ? noteNum : 0;
    setNoteDraft(String(Math.max(0, Math.round(safe))));
  }, [noteNum]);

  useEffect(() => {
    if (elementsToDisplay) {
      const displayAvatar = elementsToDisplay.find(
        element => element.type === 'avatar',
      );
      const displayAvatarType = displayAvatar?.display.find(
        element => element.checked,
      ).type;
      const displayLabel = elementsToDisplay.find(
        element => element.type === 'label',
      );
      const displaySecondary = elementsToDisplay.find(
        element => element.type === 'secondary',
      );
      setDisplay({
        avatar: {
          checked: displayAvatar?.checked,
          type: displayAvatarType,
          maskPicIndex: Number.isFinite(displayAvatar?.maskPicIndex)
            ? displayAvatar.maskPicIndex
            : null,
          maskFit: displayAvatar?.maskFit || 'contain',
          maskInvert: displayAvatar?.maskInvert === true,
          maskRotate: Number.isFinite(displayAvatar?.maskRotate)
            ? displayAvatar.maskRotate
            : 0,
        },
        label: {
          checked: displayLabel?.checked,
          position: displayLabel?.position || 'bas',
        },
        secondary: normalizeSecondaryEntries(displaySecondary),
      });
    }
  }, [elementsToDisplay]);

  useEffect(() => {
    const avatarAsset = getItemPicAsset(link, 0);
    const maskAsset = Number.isFinite(display.avatar?.maskPicIndex)
      ? getItemPicAsset(link, display.avatar.maskPicIndex)
      : null;
    if (avatarAsset?.url) {
      fetchImage(avatarAsset.url, setAvatar);
    } else {
      setAvatar('');
    }
    if (maskAsset?.url) {
      fetchImage(maskAsset.url, setAvatarMask);
    } else {
      setAvatarMask('');
    }
    setAvatarBgColor(avatarAsset?.bgColor ?? null);
    setAvatarIconSlug(avatarAsset?.iconSlug ?? null);
  }, [link, display.avatar?.maskPicIndex]);

  useEffect(() => {
    if (!avatarMask || !display.avatar?.maskRotate) {
      setRotatedAvatarMask('');
      return undefined;
    }

    let active = true;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (!active) return;
      const normalized = ((display.avatar.maskRotate % 360) + 360) % 360;
      const swapAxes = normalized === 90 || normalized === 270;
      const canvas = document.createElement('canvas');
      canvas.width = swapAxes ? img.height : img.width;
      canvas.height = swapAxes ? img.width : img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setRotatedAvatarMask('');
        return;
      }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((normalized * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      setRotatedAvatarMask(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      if (active) {
        setRotatedAvatarMask('');
      }
    };
    img.src = avatarMask;
    return () => {
      active = false;
    };
  }, [avatarMask, display.avatar?.maskRotate]);

  const resolvedMaskUrl =
    display.avatar?.maskRotate && rotatedAvatarMask
      ? rotatedAvatarMask
      : avatarMask;
  const resolvedMaskFit = display.avatar?.maskFit || 'contain';
  const resolvedMaskSize =
    resolvedMaskFit === 'cover'
      ? 'cover'
      : resolvedMaskFit === 'stretch'
        ? '100% 100%'
        : 'contain';
  const resolvedMaskPosition = resolvedMaskFit === 'fixed' ? '0 0' : 'center';
  const resolvedMaskRepeat = resolvedMaskFit === 'fixed' ? 'repeat' : 'no-repeat';
  const avatarMaskStyle = resolvedMaskUrl
    ? display.avatar?.maskInvert === true
      ? {
          WebkitMaskImage: `url(${resolvedMaskUrl}), linear-gradient(#fff 0 0)`,
          maskImage: `url(${resolvedMaskUrl}), linear-gradient(#fff 0 0)`,
          WebkitMaskRepeat: `${resolvedMaskRepeat}, no-repeat`,
          maskRepeat: `${resolvedMaskRepeat}, no-repeat`,
          WebkitMaskMode: 'luminance, luminance',
          maskMode: 'luminance, luminance',
          WebkitMaskSize: `${resolvedMaskSize}, 100% 100%`,
          maskSize: `${resolvedMaskSize}, 100% 100%`,
          WebkitMaskPosition: `${resolvedMaskPosition}, 0 0`,
          maskPosition: `${resolvedMaskPosition}, 0 0`,
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }
      : {
          WebkitMaskImage: `url(${resolvedMaskUrl})`,
          maskImage: `url(${resolvedMaskUrl})`,
          WebkitMaskRepeat: resolvedMaskRepeat,
          maskRepeat: resolvedMaskRepeat,
          WebkitMaskSize: resolvedMaskSize,
          maskSize: resolvedMaskSize,
          WebkitMaskPosition: resolvedMaskPosition,
          maskPosition: resolvedMaskPosition,
          WebkitMaskMode: 'luminance',
          maskMode: 'luminance',
        }
    : {};

  // useEffect(() => {
  //   const arr = link?.has_num.map(numObj => numObj[Object.keys(numObj)]);
  //   setLinkNum(arr);
  // }, [link]);


  const commitNoteDraft = () => {
    if (typeof onChangeNoteNum !== 'function') {
      setEditingNoteNum(false);
      return;
    }
    const parsed = parseInt(noteDraft, 10);
    const safe = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    onChangeNoteNum(safe);
    setEditingNoteNum(false);
  };

  const labelPosition = display.label?.position || 'bas';
  const showTopLabel = display.label.checked && labelPosition === 'haut';
  const showBottomLabel = display.label.checked && labelPosition === 'bas';
  const showLeftLabel = display.label.checked && labelPosition === 'gauche';
  const showRightLabel = display.label.checked && labelPosition === 'droite';
  const showOverlayLabel = display.label.checked && labelPosition === 'overlay';
  const noteNumPositionClass =
    noteNumPosition === 'top-left'
      ? 'top-05 left-05'
      : noteNumPosition === 'bottom-right'
        ? 'bottom-05 right-05'
        : noteNumPosition === 'bottom-left'
          ? 'bottom-05 left-05'
          : 'top-05 right-05';

  const getSecondaryContainerStyles = useCallback(entry => {
    const caseType = entry?.displayCase || 'case pleine largeur';
    const bgColorStyle = entry?.bgColor
      ? {
          backgroundColor:
            entry.bgColor === 'transparent'
              ? 'transparent'
              : `rgb(var(--${entry.bgColor}) / 0.35)`,
        }
      : {};
    const bgPicAsset = Number.isFinite(entry?.bgPicIndex)
      ? getItemPicAsset(link, entry.bgPicIndex)
      : null;
    const bgMaskAsset = Number.isFinite(entry?.bgMaskPicIndex)
      ? getItemPicAsset(link, entry.bgMaskPicIndex)
      : null;
    const bgPicUrl = bgPicAsset?.url || null;
    const bgMaskUrl = bgMaskAsset?.url || null;
    const bgPicFit = entry?.bgPicFit || 'content';
    const bgMaskFit = entry?.bgMaskFit || 'contain';
    const bgMaskRotate = Number.isFinite(entry?.bgMaskRotate)
      ? entry.bgMaskRotate
      : 0;
    const maskSize =
      bgMaskFit === 'cover'
        ? 'cover'
        : bgMaskFit === 'stretch'
          ? '100% 100%'
          : bgMaskFit === 'fixed'
            ? 'auto'
            : 'contain';
    const maskPosition = bgMaskFit === 'fixed' ? '0 0' : 'center';
    const maskRepeat = bgMaskFit === 'fixed' ? 'repeat' : 'no-repeat';
    const bgSize =
      bgPicFit === 'cover'
        ? 'cover'
        : bgPicFit === 'stretch'
          ? '100% 100%'
          : bgPicFit === 'contain'
            ? 'contain'
            : 'auto';

    const useMask = caseType === 'masque' && Boolean(bgMaskUrl);
    const maskStyle = useMask
      ? entry?.bgMaskInvert === true
        ? {
            WebkitMaskImage: `url(${bgMaskUrl}), linear-gradient(#fff 0 0)`,
            maskImage: `url(${bgMaskUrl}), linear-gradient(#fff 0 0)`,
            WebkitMaskRepeat: `${maskRepeat}, no-repeat`,
            maskRepeat: `${maskRepeat}, no-repeat`,
            WebkitMaskMode: 'luminance, luminance',
            maskMode: 'luminance, luminance',
            WebkitMaskSize: `${maskSize}, 100% 100%`,
            maskSize: `${maskSize}, 100% 100%`,
            WebkitMaskPosition: `${maskPosition}, 0 0`,
            maskPosition: `${maskPosition}, 0 0`,
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }
        : {
            WebkitMaskImage: `url(${bgMaskUrl})`,
            maskImage: `url(${bgMaskUrl})`,
            WebkitMaskRepeat: maskRepeat,
            maskRepeat: maskRepeat,
            WebkitMaskSize: maskSize,
            maskSize: maskSize,
            WebkitMaskPosition: maskPosition,
            maskPosition: maskPosition,
            WebkitMaskMode: 'luminance',
            maskMode: 'luminance',
          }
      : {};

    return {
      caseType,
      bgPicUrl,
      bgSize,
      bgColorStyle,
      maskStyle,
      bgMaskRotate,
    };
  }, [link]);

  const secondaryNodesByPosition = React.useMemo(() => {
    const nodes = { haut: [], bas: [], gauche: [], droite: [] };
    if (!link || !Array.isArray(display.secondary)) {
      return nodes;
    }

    display.secondary.forEach(entry => {
      if (entry?.checked !== true || entry?.source?.kind !== 'widget') {
        return;
      }
      const widgetType = entry.source.widgetType;
      const widgetId = entry.source.widgetId;
      if (!widgetType || !widgetId) {
        return;
      }
      const collection =
        widgetType === 'text'
          ? link.text || []
          : widgetType === 'has_num'
            ? link.has_num || []
            : widgetType === 'conditions'
              ? link.conditions || []
              : [];
      const sourceEntry = collection.find(obj => getId(obj) === widgetId);
      const secondaryWidget = sourceEntry?.[widgetId] || null;
      if (!secondaryWidget) {
        return;
      }

      let node = null;
      if (widgetType === 'text') {
        node = (
          <div
            className='text-center text-contrast text-opacity-75 overflow-hidden text-sm'
            onClick={e => e.stopPropagation()}
          >
            <div className='line-clamp-4'>
              <TextMd
                str={String(secondaryWidget?.text || '').slice(
                  0,
                  entry.charLimit || 120,
                )}
                textId={widgetId}
                itemId={linkId}
              />
            </div>
          </div>
        );
      } else if (widgetType === 'has_num') {
        node = (
          <div className='w-full h-full' onClick={e => e.stopPropagation()}>
            <NumGlobal
              numId={widgetId}
              num={secondaryWidget}
              itemId={linkId}
              fieldInGrid={{ displayType: entry.displayType || 'little' }}
            />
          </div>
        );
      } else if (widgetType === 'conditions') {
        node = (
          <div className='w-full h-full' onClick={e => e.stopPropagation()}>
            <ConditionGlobal
              itemId={linkId}
              conditionId={widgetId}
              condition={secondaryWidget}
              fieldInGrid={{ displayType: entry.displayType || 'little' }}
            />
          </div>
        );
      }

      if (!node) {
        return;
      }
      const { caseType, bgPicUrl, bgSize, bgColorStyle, maskStyle, bgMaskRotate } =
        getSecondaryContainerStyles(entry);
      const wrappedNode = (
        <div
          className={`relative overflow-hidden w-full ${
            caseType === 'case arrondie' ? 'rounded p-05' : ''
          }`}
          style={{
            minHeight: 34,
          }}
        >
          {(entry?.bgColor || bgPicUrl) && (
            <div
              className={`absolute inset-0 ${
                caseType === 'case arrondie' ? 'rounded' : ''
              }`}
              style={{
                ...bgColorStyle,
                ...(maskStyle || null),
                transform:
                  caseType === 'masque' && bgMaskRotate
                    ? `rotate(${bgMaskRotate}deg)`
                    : undefined,
              }}
            />
          )}
          {bgPicUrl && (
            <div
              className={`absolute inset-0 ${
                caseType === 'case arrondie' ? 'rounded' : ''
              }`}
              style={{
                backgroundImage: `url(${bgPicUrl})`,
                backgroundPosition: 'center',
                backgroundSize: bgSize,
                backgroundRepeat: 'no-repeat',
                ...(maskStyle || null),
                transform:
                  caseType === 'masque' && bgMaskRotate
                    ? `rotate(${bgMaskRotate}deg)`
                    : undefined,
              }}
            />
          )}
          <div className='relative z-10'>{node}</div>
        </div>
      );
      const position = entry.position || 'bas';
      if (!nodes[position]) {
        nodes[position] = [];
      }
      nodes[position].push(
        <React.Fragment key={entry.id || widgetId}>{wrappedNode}</React.Fragment>,
      );
    });

    return nodes;
  }, [display.secondary, link, linkId, getSecondaryContainerStyles]);

  const labelNode = (
    <div
      className={`font-condensed font-bold text-lg text-center ${
        showOverlayLabel
          ? display.avatar.type === 'pleine largeur'
            ? 'font-normal text-contrast-l leading-4 text-base text-shadow absolute w-full h-full h-1/2 bottom-0 bg-gradient-to-t from-main to-transparent flex items-end justify-center pb-05'
            : 'absolute left-1 right-1 bottom-1 rounded bg-main bg-opacity-65 text-sm leading-4 px-05 py-03 text-contrast'
          : ''
      }`}
    >
      {link?.label}
    </div>
  );

  const avatarNode = display.avatar.checked ? (
    (() => {
      const isRound =
        display.avatar.type === 'rond' || display.avatar.type === 'arrondi carré';
      const avatarSize = isRound && reducedMinAvatarSize > 0 ? reducedMinAvatarSize : 55;
      const iconSize = display.avatar.type === 'pleine largeur'
        ? 72
        : isRound
          ? Math.max(18, Math.round(avatarSize * 0.72))
          : 48;

      return (
    <div
      style={{
        ...(avatarBgColor
          ? getAvatarBackgroundStyle(avatarBgColor)
          : { backgroundColor: 'rgba(var(--main), 0.5)' }),
        backgroundImage: avatar && !avatarIconSlug ? `url(${avatar})` : undefined,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        height:
          display.avatar.type === 'rond' || display.avatar.type === 'arrondi carré'
            ? avatarSize
            : display.avatar.type === 'pleine largeur'
              ? 'calc(100% + 2em)'
              : '100%',
        width:
          display.avatar.type === 'rond' || display.avatar.type === 'arrondi carré'
            ? avatarSize
            : display.avatar.type === 'pleine largeur'
              ? 'calc(100% + 2em)'
              : '100%',
        minHeight:
          reducedMinAvatarSize > 0 &&
          (display.avatar.type === 'arrondi' ||
            display.avatar.type === 'pleine largeur')
            ? reducedMinAvatarSize
            : undefined,
        minWidth:
          reducedMinAvatarSize > 0 &&
          (display.avatar.type === 'arrondi' ||
            display.avatar.type === 'pleine largeur')
            ? reducedMinAvatarSize
            : undefined,
        ...avatarMaskStyle,
      }}
      className={`${
        display.avatar.type === 'rond'
          ? 'rounded-full'
          : display.avatar.type === 'arrondi' ||
              display.avatar.type === 'arrondi carré'
            ? 'rounded'
            : ''
      } ${display.avatar.type === 'pleine largeur' ? '-mx-1 -my-05' : 'shadow'} flex items-center justify-center`}
    >
      {avatarIconSlug &&
        renderIconBySlug(avatarIconSlug, {
          size: iconSize,
          color: 'var(--contrast)',
        })}
    </div>
      );
    })()
  ) : null;

  const sideNode = nodes => {
    if (!nodes?.length) return null;
    return (
      <div className='flex-1 h-full flex flex-col items-center justify-center gap-05'>
        {React.Children.toArray(nodes)}
      </div>
    );
  };

  return (
    <div
      ref={ref}
      className={`h-full w-full justify-center items-center flex gap-02 items-center flex-col px-1 py-05 flex-1 cursor-pointer ${
        display.avatar.type === 'pleine largeur' || showOverlayLabel
          ? 'relative'
          : ''
      } ${noteNumEnabled ? 'relative' : ''}`}
      onClick={() => {
        dispatch(setDrawerOpen(linkId));
      }}
    >
      {noteNumEnabled && (
        <div
          className={`absolute ${noteNumPositionClass} z-10 board-grid-no-drag flex items-center gap-02 text-xs group`}
          onMouseDown={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          {noteCanEdit ? (
            <div
              className='px-02 cursor-pointer hover:opacity-70 select-none opacity-0 group-hover:opacity-100 transition'
              onClick={() => {
                if (typeof onChangeNoteNum !== 'function') return;
                const current = Number.isFinite(noteNum) ? noteNum : 0;
                onChangeNoteNum(Math.max(0, current - 1));
              }}
            >
              -
            </div>
          ) : null}
          <div
            className='min-w-18 text-center rounded-full bg-main bg-opacity-70 flex items-center justify-center h-2 w-2 shadow text-contrast'
            onClick={() => {
              if (!noteCanEdit) return;
              if (typeof onChangeNoteNum !== 'function') return;
              setEditingNoteNum(true);
            }}
            title={noteCanEdit ? 'Cliquer pour modifier' : undefined}
          >
            {noteCanEdit && editingNoteNum ? (
              <input
                className='w-px44 text-center rounded-full bg-main bg-opacity-60 text-contrast text-opacity-90 px-02 py-px board-grid-no-drag'
                type='number'
                inputMode='numeric'
                min={0}
                step={1}
                value={noteDraft}
                autoFocus
                onClick={e => e.stopPropagation()}
                onChange={e => setNoteDraft(e.target.value)}
                onBlur={commitNoteDraft}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitNoteDraft();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    const safe = Number.isFinite(noteNum) ? noteNum : 0;
                    setNoteDraft(String(Math.max(0, Math.round(safe))));
                    setEditingNoteNum(false);
                  }
                }}
              />
            ) : (
              <span className='select-none'>
                {Number.isFinite(noteNum) ? noteNum : 0}
              </span>
            )}
          </div>
          {noteCanEdit ? (
            <div
              className='px-02 cursor-pointer hover:opacity-70 select-none opacity-0 group-hover:opacity-100 transition'
              onClick={() => {
                if (typeof onChangeNoteNum !== 'function') return;
                const current = Number.isFinite(noteNum) ? noteNum : 0;
                onChangeNoteNum(Math.max(0, current + 1));
              }}
            >
              +
            </div>
          ) : null}
        </div>
      )}
      {(showTopLabel || secondaryNodesByPosition.haut.length > 0) && (
        <div className='w-full flex flex-col items-center justify-center gap-05'>
          {showTopLabel && labelNode}
          {secondaryNodesByPosition.haut}
        </div>
      )}
      {(showLeftLabel ||
        showRightLabel ||
        secondaryNodesByPosition.gauche.length > 0 ||
        secondaryNodesByPosition.droite.length > 0) ? (
        <div className='w-full h-full flex items-center justify-center gap-05'>
          {sideNode([
            ...(showLeftLabel ? [labelNode] : []),
            ...secondaryNodesByPosition.gauche,
          ])}
          {avatarNode}
          {sideNode([
            ...(showRightLabel ? [labelNode] : []),
            ...secondaryNodesByPosition.droite,
          ])}
        </div>
      ) : (
        avatarNode
      )}
      {showOverlayLabel && labelNode}
      {(showBottomLabel || secondaryNodesByPosition.bas.length > 0) && (
        <div className='w-full flex flex-col items-center justify-center gap-05'>
          {showBottomLabel && labelNode}
          {secondaryNodesByPosition.bas}
        </div>
      )}
    </div>
  );
};
